import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { GraduationCap, Sparkles, Heart, Compass, Check } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";

export default function EcoleCocoon() {
  const { t } = useTranslation();
  const e = t("ecoleCocoon", { returnObjects: true });
  const rows = Array.isArray(e.rows) ? e.rows : [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl tracking-tight whitespace-nowrap" data-testid="ecole-brand"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/manifeste" className="text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.manifeste")}</Link>
            <Link to="/entrer" data-testid="ecole-enter-btn" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full">{t("nav.entrer")}</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-40 pb-12 text-center">
        <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="text-primary/80 tracking-[0.3em] uppercase text-xs mb-6" data-testid="ecole-eyebrow">{e.eyebrow}</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6" data-testid="ecole-title">
          {e.title}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="text-foreground/60 leading-[1.9] text-base md:text-lg max-w-2xl mx-auto">
          {e.intro}
        </motion.p>
      </section>

      {/* Two leads */}
      <section className="max-w-5xl mx-auto px-6 pb-14 grid gap-5 md:grid-cols-2" data-testid="ecole-leads">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="glass rounded-3xl border border-white/10 p-7">
          <span className="w-11 h-11 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center text-foreground/70 mb-4">
            <GraduationCap size={20} strokeWidth={1.6} />
          </span>
          <p className="text-foreground/65 leading-[1.85]">{e.schoolLead}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }} className="glass frame-pulse rounded-3xl border border-primary/20 p-7">
          <span className="w-11 h-11 rounded-xl bg-primary/12 border border-primary/25 flex items-center justify-center text-primary mb-4">
            <Sparkles size={20} strokeWidth={1.6} />
          </span>
          <p className="text-foreground/75 leading-[1.85]">{e.cocoonLead}</p>
        </motion.div>
      </section>

      {/* Comparison table */}
      <section className="max-w-5xl mx-auto px-6 pb-16" data-testid="ecole-table">
        <h2 className="font-display italic text-gradient-orange text-2xl md:text-3xl text-center mb-8">{e.tableTitle}</h2>

        <div className="glass rounded-3xl border border-white/10 overflow-hidden">
          {/* header row */}
          <div className="hidden sm:grid grid-cols-[1.1fr_1.4fr_1.4fr] border-b border-white/10 bg-white/[0.03]">
            <div className="px-5 py-4 text-[0.7rem] uppercase tracking-[0.2em] text-foreground/40">{e.colCriteria}</div>
            <div className="px-5 py-4 text-[0.7rem] uppercase tracking-[0.2em] text-foreground/40 border-l border-white/10">{e.colSchool}</div>
            <div className="px-5 py-4 text-[0.7rem] uppercase tracking-[0.2em] text-primary/80 border-l border-white/10">{e.colCocoon}</div>
          </div>

          {rows.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`ecole-row-${i}`}
              className={`sm:grid sm:grid-cols-[1.1fr_1.4fr_1.4fr] ${i < rows.length - 1 ? "border-b border-white/10" : ""}`}
            >
              <div className="px-5 pt-5 pb-2 sm:py-5 font-display text-lg text-foreground/90">{r.c}</div>
              <div className="px-5 py-2 sm:py-5 sm:border-l border-white/10 text-sm text-foreground/55 leading-relaxed">
                <span className="sm:hidden block text-[0.65rem] uppercase tracking-[0.18em] text-foreground/35 mb-1">{e.colSchool}</span>
                {r.s}
              </div>
              <div className="px-5 pt-2 pb-5 sm:py-5 sm:border-l border-primary/15 text-sm text-foreground/75 leading-relaxed bg-primary/[0.04]">
                <span className="sm:hidden block text-[0.65rem] uppercase tracking-[0.18em] text-primary/60 mb-1">{e.colCocoon}</span>
                <span className="inline-flex gap-2">
                  <Check size={15} className="text-primary flex-shrink-0 mt-0.5" />
                  <span>{r.m}</span>
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Summary + respect note */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center mb-10">
          <p className="uppercase tracking-[0.22em] text-[0.7rem] text-primary/70 mb-3">{e.summaryTitle}</p>
          <p className="text-foreground/70 leading-[1.9] text-base md:text-lg">{e.summary}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="glass rounded-3xl border border-primary/20 p-7 flex items-start gap-4" data-testid="ecole-respect">
          <span className="w-11 h-11 rounded-full bg-primary/12 border border-primary/25 flex items-center justify-center text-primary flex-shrink-0">
            <Heart size={18} className="fill-primary/40" />
          </span>
          <p className="font-display italic text-lg md:text-xl leading-relaxed text-foreground/85">{e.respect}</p>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 pb-28 text-center">
        <Link to="/entrer" data-testid="ecole-cta-btn" className="btn-frost-orange inline-flex items-center gap-3 px-9 py-4 rounded-full font-semibold text-lg">
          {e.cta} <Compass size={18} />
        </Link>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row justify-between gap-4 text-sm text-foreground/40">
          <Link to="/" className="font-display text-lg"><CocoonMark letterClassName="text-foreground/60" withMind /></Link>
          <span>{t("landing.footerTagline")}</span>
        </div>
      </footer>
    </div>
  );
}
